import {merge, Observable} from 'rxjs';
import {map, filter, delay, scan} from 'rxjs/operators';
import {orderUtil} from '@/helper/order';
import {uiMsg$} from '@/ui';
import {wsLogout$} from '@/user/auth-ws';
import {contractSnapshotOrders$, contractIncrementalOrders$} from '@/orders-contract/orders-contract';
import {wsOpened$} from '@/ws';
import type {ws} from '@/types';
import type {FUOrderActiveRemove} from '@/spot-utils/orders-active-cache';
type ContractOrderRaw = ws.ContractOrderRaw;

export type Cache = Map<string, ContractOrderRaw>;
type Action = (map: Cache) => Cache;

const resetAction$ = merge(wsLogout$, wsOpened$).pipe(
  map(
    (): Action => (cache: Cache) => {
      cache.clear();
      return cache;
    }
  )
);

const removeByUserAction$ = uiMsg$.pipe(
  filter((o) => o.type === 'order-active-remove'),
  map<FUOrderActiveRemove, Action>((data) => (cache: Cache) => {
    cache.delete(data.orderID);
    return cache;
  })
);

const snapshotOrders$ = contractSnapshotOrders$.pipe(map(({orders: {open}}) => open));
const snapshotAction$ = createSnapshotAction$(snapshotOrders$, false);
const snapshotBOAction$ = createSnapshotAction$(snapshotOrders$, true);

const incrementalActiveOrders$ = contractIncrementalOrders$.pipe(
  map(({orders: {open, closed}}) => [...closed, ...open])
);
const updateAction$ = createUpdateAction$(incrementalActiveOrders$, false);
const updateBOAction$ = createUpdateAction$(incrementalActiveOrders$, true);

const incrementalClosedOrders$ = contractIncrementalOrders$.pipe(
  map(({orders: {closed}}) => closed.filter((order) => order.execStatus !== 'ReplaceToCanceled'))
);
const removeAction$ = createRemoveAction$(incrementalClosedOrders$);
const removeBOAction$ = createRemoveBOAction$(incrementalClosedOrders$);

export const contractActiveOrdersCache$ = merge<Action[]>(
  snapshotAction$,
  updateAction$,
  removeAction$,
  resetAction$,
  removeByUserAction$
).pipe(scan((p, c) => c(p), new Map<string, ContractOrderRaw>()));

export const contractActiveOrdersCacheBO$ = merge<Action[]>(
  snapshotBOAction$,
  updateBOAction$,
  removeBOAction$,
  resetAction$,
  removeByUserAction$
).pipe(scan((p, c) => c(p), new Map<string, ContractOrderRaw>()));

function isDisplayOrder(raw: ContractOrderRaw, type: string, isBracketOrders: boolean) {
  // 先决条件
  const acceptable =
    ['New', 'Replace', 'Cancel'].indexOf(raw.action) > -1 &&
    ['ReplaceRejected', 'CancelRejected'].indexOf(raw.execStatus) < 0;
  if (!acceptable) {
    return false;
  }
  //TODO solve CTI-1100
  if (orderUtil.isBracketOrders(raw.ordType)) {
    return isBracketOrders;
  }

  if (type === 'snapshot') {
    // 不显示已结束的订单
    return ['New', 'PartiallyFilled'].indexOf(raw.ordStatus) > -1;
  }

  if (type === 'incremental') {
    return ['New', 'PartiallyFilled', 'Filled', 'Canceled'].indexOf(raw.ordStatus) > -1;
  }

  return false;
}

function createSnapshotAction$(snapshotOrders$: Observable<ContractOrderRaw[]>, isBracketOrders: boolean) {
  return snapshotOrders$.pipe(
    map((orders) => {
      const cleanOrders = isBracketOrders ? cleanSnapshotBoOrders(orders) : orders;
      return cleanOrders.filter((order) => isDisplayOrder(order, 'snapshot', isBracketOrders));
    }),
    filter((orders) => orders.length > 0),
    map(convertSnapshotAction)
  );
}

function createUpdateAction$(incrementalOrders$: Observable<ContractOrderRaw[]>, isBracketOrders: boolean) {
  return incrementalOrders$.pipe(
    map((orders) => orders.filter((order) => isDisplayOrder(order, 'incremental', isBracketOrders))),
    filter((orders) => orders.length > 0),
    map(convertUpdateAction)
  );
}

function createRemoveAction$(incrementalOrders$: Observable<ContractOrderRaw[]>) {
  return incrementalOrders$.pipe(
    map((orders) => orders.filter((order) => isDisplayOrder(order, 'incremental', false))),
    filter((orders) => orders.length > 0),
    map(convertRemoveAction),
    delay(5000)
  );
}

function createRemoveBOAction$(incrementalOrders$: Observable<ContractOrderRaw[]>) {
  return incrementalOrders$.pipe(
    map((orders) => orders.filter((order) => orderUtil.isBracketOrders(order.ordType))),
    filter((orders) => orders.length > 0),
    map(convertRemoveBOAction),
    delay(5000)
  );
}

function convertSnapshotAction(orders: ContractOrderRaw[]): Action {
  return function (cache: Cache) {
    orders.forEach((o) => cache.set(o.orderID, o));
    return cache;
  };
}

function convertUpdateAction(orders: ContractOrderRaw[]): Action {
  return function (cache: Cache) {
    orders.forEach((o) => cache.set(o.orderID, o));
    return cache;
  };
}

function convertRemoveAction(orders: ContractOrderRaw[]): Action {
  return function (cache: Cache) {
    orders.forEach((o) => cache.delete(o.orderID));
    return cache;
  };
}

function convertRemoveBOAction(orders: ContractOrderRaw[]): Action {
  return function (cacheBO: Cache) {
    const items = cacheBO.toArray<ContractOrderRaw>();
    orders.forEach((order) => {
      const po = findPrimaryOrders(items, order);
      if (po) {
        const {tpOrder, slOrder} = findTpSlOrders(items, po);
        if (!tpOrder && !slOrder) {
          cacheBO.delete(po.orderID);
        }
        if (
          tpOrder &&
          slOrder &&
          orderUtil.isFinalOrder(tpOrder.ordStatus) &&
          orderUtil.isFinalOrder(slOrder.ordStatus)
        ) {
          cacheBO.delete(po.orderID);
          cacheBO.delete(tpOrder.orderID);
          cacheBO.delete(slOrder.orderID);
        }
      }
    });
    return cacheBO;
  };
}

function findPrimaryOrders(items: ContractOrderRaw[], order: ContractOrderRaw) {
  if (order.ordType === 'Bracket') {
    return order;
  }

  const orderID = order.clOrdID.slice(0, -3);
  return items.find((o) => o.orderID === orderID);
}

function cleanSnapshotBoOrders(orders: ContractOrderRaw[]) {
  const primaryOrders = orders.filter((o) => o.ordType === 'Bracket');
  const removeOrderIDs = new Set<string>();
  primaryOrders.forEach((po) => {
    const {tpOrder, slOrder} = findTpSlOrders(orders, po);
    if (!tpOrder) {
      console.log(
        `BO has no TP: orderID: ${po.orderID}, priceEp: ${po.priceEp}, orderQty: ${po.orderQty}, ordStatus: ${po.ordStatus}`
      );
    }
    if (!slOrder) {
      console.log(
        `BO has no SL: orderID: ${po.orderID}, priceEp: ${po.priceEp}, orderQty: ${po.orderQty}, ordStatus: ${po.ordStatus}`
      );
    }
    if (!tpOrder || !slOrder) {
      // 不显示不完整的括号订单组
      removeOrderIDs.add(po.orderID);
      if (tpOrder) {
        removeOrderIDs.add(tpOrder.orderID);
      }
      if (slOrder) {
        removeOrderIDs.add(slOrder.orderID);
      }
      return;
    }

    if (
      orderUtil.isFinalOrder(po.ordStatus) &&
      tpOrder &&
      orderUtil.isFinalOrder(tpOrder.ordStatus) &&
      slOrder &&
      orderUtil.isFinalOrder(slOrder.ordStatus)
    ) {
      removeOrderIDs.add(po.orderID);
      removeOrderIDs.add(tpOrder.orderID);
      removeOrderIDs.add(slOrder.orderID);
    }
  });
  return orders.filter((o) => !removeOrderIDs.has(o.orderID));
}

function findTpSlOrders(orders: ContractOrderRaw[], po: ContractOrderRaw) {
  return {
    tpOrder: orders.find((o) => o.clOrdID === po.orderID + '_TP'),
    slOrder: orders.find((o) => o.clOrdID === po.orderID + '_SL'),
  };
}
